import React from "react";
import * as PropTypes from "prop-types";
import clsx from "clsx";
import { useTranslate } from "react-polyglot";
import { useHistory } from "react-router-dom";
import { makeStyles, Typography, Box } from "@material-ui/core";
import useCompanyTitle from "../../../../hooks/useCompanyTitle.js";
import { useCourseProvider } from "../../../../Providers/Data/CourseProvider.jsx";
import FlexboxHorizontal from "../../../../SharedComponents/FlexboxHorizontal.jsx";
import LinearProgressBar from "../../../../SharedComponents/LinearProgressBar.jsx";
import { randomColor } from "../../../../Utils/misc.js";
import { ReactComponent as IconLock } from "../../../../assets/Icons/ic_lock_2.svg";

const useStyles = makeStyles(() => ({
  branchCard: {
    alignItems: "center",
    padding: 12,
    marginBottom: 12,
    borderLeft: "3px solid",
    borderRadius: 12,
    color: "#1B1C1C",
    backgroundColor: "#fff",
    cursor: "pointer",
    "&:hover": {
      background: `linear-gradient(rgba(0,0,0,0.03), rgba(0,0,0,0.03)), #fff`,
    },
  },
  branchCardDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  branchImageBox: {
    width: 110,
    height: 110,
    borderRadius: 12,
    overflow: "hidden",
    flexShrink: 0,
    marginLeft: 12,
  },
  image: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "cover",
    objectPosition: "center",
  },
  branchName: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.4,
    height: 45,
    minWidth: 357,
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  },
  numberBranch: {
    width: 20,
    textAlign: "center",
    fontSize: 14,
  },
  branchNameBox: {
    marginLeft: 8,
  },
  branchPercentBox: {
    marginLeft: 4,
  },
  branchPercent: {
    fontSize: 14,
    "&.MuiTypography-colorSecondary": {
      color: "#58A065",
    },
  },
  deckNumber: {
    fontSize: 14,
    color: "#54606B",
    position: "relative",
    marginLeft: 24,
    "&::after": {
      content: "'•'",
      color: "#C5C7C9",
      top: 0,
      left: -12,
      position: "absolute",
    },
  },
  branchProgress: {
    "& .MuiLinearProgress-bar": {
      borderRadius: 3,
    },
    "& .MuiLinearProgress-barColorSecondary": {
      backgroundColor: "#58A065",
    },
  },
  branchProgressDisabled: {
    "& .MuiLinearProgress-bar": {
      borderRadius: 3,
    },
    "&.MuiLinearProgress-colorPrimary": {
      backgroundColor: "#54606B",
      opacity: 0.1,
    },
  },
}));

/**
 * @param {Branch} branch
 * @param {Number} index
 * @returns {*}
 * @constructor
 */
const BranchCardHorizontal = ({ branch, index }) => {
  const classes = useStyles();
  const t = useTranslate();
  const { saveCurrentBranch } = useCourseProvider();
  const history = useHistory();

  useCompanyTitle("title.home");

  /** */
  const onClickBranch = () => {
    if (!branch.open) return;
    saveCurrentBranch(branch);
    history.push("/decks");
  };

  /** */
  const pluralizeLessons = (count) => {
    if (count % 10 === 1 && count % 100 !== 11) return t("lessonsCount_one", { smart_count: count });
    if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100))
      return t("lessonsCount_few", { smart_count: count });
    return t("lessonsCount_many", { smart_count: count });
  };

  return (
    <FlexboxHorizontal
      onClick={() => onClickBranch(branch)}
      className={clsx(classes.branchCard, {
        [classes.branchCardDisabled]: !branch.open,
      })}
      style={{
        borderColor: branch.color || randomColor(),
      }}
    >
      <Typography className={classes.numberBranch}>{index + 1}</Typography>
      <Box className={classes.branchNameBox}>
        <Typography className={classes.branchName}>{branch.name}</Typography>
        <FlexboxHorizontal alignItems="center">
          <LinearProgressBar
            className={branch.open ? classes.branchProgress : classes.branchProgressDisabled}
            variant="determinate"
            value={branch.percent}
            height={6}
            color={branch.percent >= 99 ? "secondary" : "primary"}
            style={{
              borderRadius: 3,
              width: 65,
            }}
            noborder
            resetBackgroundColor
          />
          {!branch.open && (
            <Box className={classes.branchPercentBox}>
              <IconLock />
            </Box>
          )}
          <Typography className={classes.deckNumber}>{pluralizeLessons(branch.decks.length)}</Typography>
        </FlexboxHorizontal>
      </Box>
      {(branch.illustrationImage || branch.illustrationImageUrl) && (
        <Box className={classes.branchImageBox}>
          <img
            className={classes.image}
            src={branch.illustrationImage?.url || branch.illustrationImageUrl}
            alt="image of topic"
          />
        </Box>
      )}
    </FlexboxHorizontal>
  );
};

BranchCardHorizontal.propTypes = {
  branch: PropTypes.object,
  index: PropTypes.number,
};

export default BranchCardHorizontal;
